import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';

/**
 * CRM-Zugriffe.
 *
 * Alle Kennzahlen kommen fertig vom Server — Forecast, Liegengeblieben-Marke,
 * Ueberfaelligkeit. Die Oberflaeche rechnet nichts nach: zwei Rechenwege fuer
 * dieselbe Zahl laufen frueher oder spaeter auseinander, und im Altsystem
 * taten sie es.
 */

export interface BoardDeal {
  id: string;
  title: string;
  value: number;
  status: string;
  rotting: boolean;
  stageEnteredAt: string;
}

export interface BoardStage {
  id: string;
  name: string;
  probability: number;
  type: string;
  deals: BoardDeal[];
  sum: number;
}

export interface Board {
  pipeline: { id: string; name: string; currency: string };
  stages: BoardStage[];
  forecast: number;
}

export const boardQuery = queryOptions({
  queryKey: ['crm', 'board'],
  queryFn: ({ signal }) => api.get<Board>('/api/crm/board', signal),
  staleTime: 5_000,
});

export interface DealDetail {
  deal: {
    id: string;
    title: string;
    value: number;
    currency: string;
    status: string;
    stageId: string;
    stageName: string;
    probability: number;
    rotting: boolean;
    lostReason: string | null;
    expectedCloseDate: string | null;
    stageEnteredAt: string;
    createdAt: string;
  };
  stages: Array<{ id: string; name: string; probability: number; type: string }>;
  contacts: Array<{
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    leadScore: number;
    status: string;
    band: string;
  }>;
  tasks: CrmTask[];
  notes: Array<{ id: string; body: string; createdAt: string }>;
}

export const dealQuery = (id: string) =>
  queryOptions({
    queryKey: ['crm', 'deal', id],
    queryFn: ({ signal }) => api.get<DealDetail>(`/api/crm/deals/${id}`, signal),
  });

export interface CrmTask {
  id: string;
  title: string;
  description: string | null;
  type: string;
  priority: string;
  status: string;
  dueAt: string | null;
  overdue: boolean;
  contactId: string | null;
  dealId: string | null;
}

export const tasksQuery = (filters: { mine?: boolean; status?: 'open' | 'done' } = {}) =>
  queryOptions({
    queryKey: ['crm', 'tasks', filters],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (filters.mine) params.set('mine', 'true');
      if (filters.status) params.set('status', filters.status);
      const query = params.toString();
      return api.get<{ items: CrmTask[] }>(`/api/crm/tasks${query ? `?${query}` : ''}`, signal);
    },
    staleTime: 5_000,
  });

export interface ActionStreamItem {
  contact: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string;
    companyId: string | null;
    companyName: string | null;
  };
  task: {
    id: string;
    title: string;
    type: string;
    priority: string;
    dueAt: string | null;
  };
}

export const actionStreamQuery = queryOptions({
  queryKey: ['crm', 'action-stream'],
  queryFn: ({ signal }) => api.get<{ items: ActionStreamItem[] }>('/api/crm/action-stream', signal),
  staleTime: 5_000,
});

export interface TimelineItem {
  kind: 'email' | 'custom' | 'activity';
  type: string;
  title: string;
  body: string | null;
  occurred_at: string;
}

export interface ContactEmailMessage {
  id: string;
  threadId: string | null;
  contactId: string | null;
  direction: 'inbound' | 'outbound';
  messageId: string;
  inReplyTo: string | null;
  fromEmail: string;
  toEmails: string[];
  subject: string | null;
  textBody: string | null;
  htmlBody: string | null;
  classification: string;
  receivedAt: string;
}

export const contactThreadQuery = (contactId: string) =>
  queryOptions({
    queryKey: ['crm', 'contact-thread', contactId],
    queryFn: ({ signal }) =>
      api.get<{ messages: ContactEmailMessage[] }>(
        `/api/crm/contacts/${contactId}/thread`,
        signal,
      ),
    staleTime: 3_000,
    refetchInterval: 6_000,
  });

export interface Timeline {
  items: TimelineItem[];
  scoreHistory: Array<{ reason: string; points: number; occurred_at: string }>;
  score: { points: number; band: string };
  tasks: CrmTask[];
}

export const timelineQuery = (contactId: string) =>
  queryOptions({
    queryKey: ['crm', 'timeline', contactId],
    queryFn: ({ signal }) => api.get<Timeline>(`/api/crm/contacts/${contactId}/timeline`, signal),
  });

export function useSendContactEmail(contactId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { subject: string; textBody: string; inReplyTo?: string | null }) =>
      api.post<{ ok: true; messageId: string }>(`/api/crm/contacts/${contactId}/send-email`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crm'] });
      void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

// --- Veraenderungen ---------------------------------------------------------

/** Nach jeder Aenderung neu laden: Forecast und Summen haengen daran. */
function invalidateBoard(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['crm'] });
}

export function useCreateDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      value: number;
      stageId?: string;
      contactIds?: string[];
      expectedCloseDate?: string | null;
    }) => api.post<{ deal: { id: string } }>('/api/crm/deals', input),
    onSuccess: () => invalidateBoard(queryClient),
  });
}

export function useMoveDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; stageId: string; lostReason?: string | null }) =>
      api.post<{ deal: BoardDeal }>(`/api/crm/deals/${input.id}/move`, {
        stageId: input.stageId,
        lostReason: input.lostReason ?? null,
      }),
    onSuccess: () => invalidateBoard(queryClient),
  });
}

export function useUpdateDeal(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { title?: string; value?: number; expectedCloseDate?: string | null }) =>
      api.patch<{ deal: BoardDeal }>(`/api/crm/deals/${id}`, input),
    onSuccess: () => invalidateBoard(queryClient),
  });
}

export function useDeleteDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: true }>(`/api/crm/deals/${id}`),
    onSuccess: () => invalidateBoard(queryClient),
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      title: string;
      type?: string;
      priority?: string;
      dueAt?: string | null;
      contactId?: string | null;
      dealId?: string | null;
    }) => api.post<{ task: CrmTask }>('/api/crm/tasks', input),
    onSuccess: () => invalidateBoard(queryClient),
  });
}

export function useCompleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ ok: true }>(`/api/crm/tasks/${id}/complete`),
    onSuccess: () => invalidateBoard(queryClient),
  });
}

export function useCreateNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; contactId?: string | null; dealId?: string | null }) =>
      api.post<{ note: { id: string } }>('/api/crm/notes', input),
    onSuccess: () => invalidateBoard(queryClient),
  });
}

export function formatMoney(value: number, currency = 'CHF'): string {
  return new Intl.NumberFormat('de-CH', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

// --- Kontakte ohne nächste Aktion -------------------------------------------

export interface ContactWithoutNextAction {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  companyName: string | null;
  leadScore: number;
  lastActivityAt: string | null;
}

/**
 * Das Gegenstück zum Action Stream: was liegen bleibt.
 *
 * Ein leerer Stream sieht nach Feierabend aus — solange man nicht daneben
 * sieht, wie viele Kontakte gar keinen nächsten Schritt haben.
 */
export const withoutNextActionQuery = queryOptions({
  queryKey: ['crm', 'without-next-action'],
  queryFn: ({ signal }) =>
    api.get<{ total: number; items: ContactWithoutNextAction[] }>(
      '/api/crm/without-next-action',
      signal,
    ),
  staleTime: 5_000,
});

/** Anruf, Termin, gesendete Mail oder Notiz festhalten — mit optionaler Folgeaufgabe. */
export function useLogActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      contactId: string;
      kind: 'call' | 'meeting' | 'email' | 'note';
      title?: string | null;
      note?: string | null;
      nextAction?: { title: string; type: string; dueAt?: string | null } | null;
    }) => api.post<{ taskId: string | null }>('/api/crm/log', input),
    onSuccess: () => {
      invalidateBoard(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['crm', 'without-next-action'] });
      void queryClient.invalidateQueries({ queryKey: ['crm', 'timeline'] });
    },
  });
}
